import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { getSocialFeeDisplayRate } from '../enums/socialFeeRateMapping.enum';
import { formatAmountWithLocale } from '../helper/common.helper';
import './FreelancerCalculationModal.scss';

interface FreelancerCalculationModalProps {
  isOpen: boolean;
  onClose: () => void;
  calculationData: any;
  freelancerId: string;
  currency?: string;
}

export const FreelancerCalculationModal: React.FC<FreelancerCalculationModalProps> = ({
  isOpen,
  onClose,
  calculationData,
  freelancerId,
  currency = 'SEK'
}) => {
  const { t, i18n } = useTranslation();
  
  // Get freelancer-specific data from API response
  const data = calculationData?.response;
  const freelancerData = data?.freelancersData?.find(
    (fd: any) => fd.createdBy?.toString() === freelancerId?.toString()
  );

  // If no freelancer data found, don't render
  if (!freelancerData) {
    return null;
  }

  // Determine if we should use SEK values (when currency is not SEK)
  const isForeignCurrency = data?.totalCalculation?.currencyName && data.totalCalculation.currencyName !== 'SEK';
  
  // Format currency - always show SEK when foreign currency
  const formatCurrency = (amount: number) => {
    if (amount === 0 || amount === null || amount === undefined) {
      return formatAmountWithLocale(0, 'SEK', i18n.language);
    }
    return formatAmountWithLocale(amount, 'SEK', i18n.language);
  };
  
  // Format original currency (for Invoice Amount and VAT fields)
  const formatCurrencyOriginal = (amount: number) => {
    if (amount === 0 || amount === null || amount === undefined) {
      return formatAmountWithLocale(0, currency, i18n.language);
    }
    return formatAmountWithLocale(amount, currency, i18n.language);
  };
  
  // Helper to get SEK amount
  const getAmount = (directAmount: number, sekAmount: number) => {
    return isForeignCurrency ? sekAmount : directAmount;
  };

  const calcData = freelancerData;

  return (
    <Modal 
      show={isOpen} 
      onHide={onClose} 
      size="lg" 
      centered 
      className="freelancer-calculation-modal"
      backdrop="static"
      keyboard={true}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {t('CALCULATION_BREAKDOWN') || 'Calculation Breakdown'}
          {freelancerData.freelancerName && (
            <div className="freelancer-name">{freelancerData.freelancerName}</div>
          )}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        <div className="calculation-breakdown-single">
          <div className="breakdown-section">
            {/* Step 1: Invoice amount inc. VAT */}
            <div className="calculation-item">
              <span className="item-label">{t('INVOICE_AMOUNT_INCLUDING_VAT') || 'Invoice Amount inc. VAT'}</span>
              <span className="item-value">{formatCurrencyOriginal(calcData.amountIncludingVat)}</span>
            </div>

            {/* Step 2: RUT/ROT Deduction (if applicable) */}
            {calcData.rutDeductionAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('RUT_DEDUCTION_AMOUNT') || 'RUT Deduction'}</span>
                <span className="item-value">-{formatCurrency(calcData.rutDeductionAmount)}</span>
              </div>
            )}

            {calcData.rootDeductionAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('ROT_DEDUCTION_AMOUNT') || 'ROT Deduction'}</span>
                <span className="item-value">-{formatCurrency(calcData.rootDeductionAmount)}</span>
              </div>
            )}

            {/* Step 3: Customer will pay (if RUT/ROT deduction exists) */}
            {(calcData.rutDeductionAmount > 0 || calcData.rootDeductionAmount > 0) && (
              <div className="calculation-item">
                <span className="item-label">{t('CUSTOMER_WILL_PAY') || 'Customer will pay'}</span>
                <span className="item-value">{formatCurrency(calcData.amountIncludingVat - (calcData.rutDeductionAmount || 0) - (calcData.rootDeductionAmount || 0))}</span>
              </div>
            )}

            {/* Step 4: VAT */}
            {calcData.vat > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('VAT') || 'VAT'} ({calcData.vatPercentage}%)</span>
                <span className="item-value">{formatCurrencyOriginal(calcData.vat)}</span>
              </div>
            )}

            {/* Step 5: Invoice amount ex. VAT */}
            <div className="calculation-item">
              <span className="item-label">{t('INVOICE_AMOUNT_EXCLUDING_VAT') || 'Invoice amount ex. VAT'}</span>
              <span className="item-value">{formatCurrency(getAmount(calcData.amount, calcData.amountInSek || calcData.amount))}</span>
            </div>

            {/* Step 6: Invozio percent fee */}
            {calcData.fee > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('INVOZIO_FEE') || 'Invozio percent fee'} ({calcData.fakturaFeePercentage}%)</span>
                <span className="item-value">{formatCurrency(getAmount(calcData.fee, calcData.feeInSEK || calcData.fee))}</span>
              </div>
            )}

            {/* Step 7: Loyalty discount on invozio fee */}
            {calcData.loyaltyDiscountPercentage > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('LOYALTY_DISCOUNT_ON_INVOZIO_FEE') || 'Loyalty discount on invozio fee'}</span>
                <span className="item-value">{calcData.loyaltyDiscountPercentage}%</span>
              </div>
            )}

            {/* Step 8: Social Fee */}
            {calcData.socialFee > 0 && (
              <div className="calculation-item">
                <span className="item-label">
                  {t('SOCIAL_FEE') || 'Social Fee'} ({getSocialFeeDisplayRate(calcData.socialFeeRateDeduction).toFixed(2)}%)
                </span>
                <span className="item-value">{formatCurrency(getAmount(calcData.socialFee, calcData.socialFeeInSek || calcData.socialFee))}</span>
              </div>
            )}

            {/* Step 9: Pension Social Fee */}
            {calcData.pensionSocialFee > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('PENSION_SOCIAL_FEE') || 'Pension Social Fee'} ({calcData.pensionFeePer}%)</span>
                <span className="item-value">{formatCurrency(getAmount(calcData.pensionSocialFee, calcData.pensionSocialFeeInSek || calcData.pensionSocialFee))}</span>
              </div>
            )}

            {/* Step 10: Pension */}
            {calcData.pension > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('PENSION') || 'Pension'}</span>
                <span className="item-value">{formatCurrency(getAmount(calcData.pension, calcData.pensionInSek || calcData.pension))}</span>
              </div>
            )}

            {/* Step 11: Gross salary incl. holiday pay */}
            <div className="calculation-item">
              <span className="item-label">{t('GROSS_SALARY_INCLUDING_HOLIDAY_PAY') || 'Gross salary incl. holiday pay'}</span>
              <span className="item-value">{formatCurrency(getAmount(calcData.gross, calcData.grossInSek || calcData.gross))}</span>
            </div>

            {/* Step 12: Tax */}
            {calcData.tax > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('INCOME_TAX') || 'Tax'} ({calcData.taxPercentage || calcData.taxRateDeduction || '30.00'}%)</span>
                <span className="item-value">{formatCurrency(getAmount(calcData.tax, calcData.taxInSek || calcData.tax))}</span>
              </div>
            )}

            {/* Step 13: Holiday Pay */}
            {calcData.holidayPayAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('HOLIDAY_PAY') || 'Holiday Pay'}</span>
                <span className="item-value">{formatCurrency(getAmount(calcData.holidayPayAmount, calcData.holidayPayAmountInSek || calcData.holidayPayAmount))}</span>
              </div>
            )}

            {/* Step 14: Express Fee */}
            {calcData.expressFee > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('EXPRESS_FEE') || 'Express Fee'}</span>
                <span className="item-value">{formatCurrency(getAmount(calcData.expressFee, calcData.expressFeeInSek || calcData.expressFee))}</span>
              </div>
            )}

            {/* Step 15: Total Working Hours */}
            {calcData.workingHours && calcData.workingHours.length > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('TOTAL_WORKING_HOURS') || 'Total Working Hours'}</span>
                <span className="item-value">{calcData.workingHours.reduce((sum: number, wh: any) => sum + (wh.workingHour || 0), 0)} {t('HOURS') || 'Hours'}</span>
              </div>
            )}

            {/* Step 16: Tax-free articles */}
            {calcData.mileageTaxFreeAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('MILEAGE_REIMBURSEMENT') || 'Mileage Reimbursement'}</span>
                <span className="item-value">{formatCurrency(getAmount(calcData.mileageTaxFreeAmount, calcData.mileageTaxFreeAmountInSek || calcData.mileageTaxFreeAmount))}</span>
              </div>
            )}

            {calcData.expenditureForMaterialsAndToolsTaxFreeAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('EXPENSES_FOR_MATERIALS_AND_TOOLS') || 'Material Tool & Expenses'}</span>
                <span className="item-value">{formatCurrency(getAmount(calcData.expenditureForMaterialsAndToolsTaxFreeAmount, calcData.expenditureForMaterialsAndToolsTaxFreeAmountInSek || calcData.expenditureForMaterialsAndToolsTaxFreeAmount))}</span>
              </div>
            )}

            {calcData.allowanceAbroadTaxFreeAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('ALLOWANCE_OUTSIDE_SWEDEN') || 'Allowance Outside Sweden'}</span>
                <span className="item-value">{formatCurrency(getAmount(calcData.allowanceAbroadTaxFreeAmount, calcData.allowanceAbroadTaxFreeAmountInSek || calcData.allowanceAbroadTaxFreeAmount))}</span>
              </div>
            )}

            {calcData.domesticAllowanceFullDayTaxFreeAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('ALLOWANCE_IN_SWEDEN') || 'Allowance In Sweden'} ({t('FULL_DAY') || 'Full Day'})</span>
                <span className="item-value">{formatCurrency(getAmount(calcData.domesticAllowanceFullDayTaxFreeAmount, calcData.domesticAllowanceFullDayTaxFreeAmountInSek || calcData.domesticAllowanceFullDayTaxFreeAmount))}</span>
              </div>
            )}

            {calcData.domesticAllowanceHalfDayTaxFreeAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('ALLOWANCE_IN_SWEDEN') || 'Allowance In Sweden'} ({t('HALF_DAY') || 'Half Day'})</span>
                <span className="item-value">{formatCurrency(getAmount(calcData.domesticAllowanceHalfDayTaxFreeAmount, calcData.domesticAllowanceHalfDayTaxFreeAmountInSek || calcData.domesticAllowanceHalfDayTaxFreeAmount))}</span>
              </div>
            )}

            {calcData.travelCostsAccordingTaxFreeAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('TRAVEL_COSTS_ACCORDING_TO_ATTACHED_PDF') || 'Travel costs according to attached PDF'}</span>
                <span className="item-value">{formatCurrency(getAmount(calcData.travelCostsAccordingTaxFreeAmount, calcData.travelCostsAccordingTaxFreeAmountInSek || calcData.travelCostsAccordingTaxFreeAmount))}</span>
              </div>
            )}

            {calcData.allowance > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('ALLOWANCE') || 'Other Allowances'}</span>
                <span className="item-value">{formatCurrency(getAmount(calcData.allowance, calcData.allowanceInSek || calcData.allowance))}</span>
              </div>
            )}

            {calcData.expense > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('EXPENSE') || 'Travel Expenses as per attached PDF'}</span>
                <span className="item-value">{formatCurrency(getAmount(calcData.expense, calcData.expenseInSek || calcData.expense))}</span>
              </div>
            )}

            {calcData.administratorFee > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('ADMINISTRATOR_FEE') || 'Administrator Fee'}</span>
                <span className="item-value">{formatCurrency(getAmount(calcData.administratorFee, calcData.administratorFeeInSek || calcData.administratorFee))}</span>
              </div>
            )}
          </div>

          {/* Step 17: Net salary incl. holiday pay */}
          <div className="breakdown-section net-salary-section">
            <div className="calculation-item">
              <span className="item-label item-label-bold">{t('NET_SALARY') || 'Net salary incl. holiday pay'}</span>
              <span className="item-value">{formatCurrency(getAmount(calcData.net, calcData.netInSek || calcData.net))}</span>
            </div>
          </div>

          {/* Working Hours Table */}
          {calcData.workingHoursList && calcData.workingHoursList.length > 0 && (
            <div className="breakdown-section">
              <h6 className="section-title">{t('WORKING_HOURS_BREAKDOWN') || 'Working Hours Breakdown'}</h6>
              <div className="working-hours-table">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('ARTICLE') || 'Article'}</th>
                      <th>{t('HOURS') || 'Hours'}</th>
                      <th>{t('AMOUNT') || 'Amount'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calcData.workingHoursList.map((wh: any, index: number) => (
                      <tr key={index}>
                        <td>{wh.articleName || wh.articleDescription || '-'}</td>
                        <td>{wh.workingHour || wh.hours || '-'}</td>
                        <td>{formatCurrency(wh.amount || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </Modal.Body>
      
      <Modal.Footer>
        <Button 
          style={{
            backgroundColor: "#6750b8",
            borderRadius: "25px",
            boxShadow: "0 1px 2px 0 rgba(16, 24, 40, 0.04)",
            display: "flex",
            padding: "10px 18px",
            justifyContent: "center",
            alignItems: "center",
            gap: "6px",
            alignSelf: "stretch",
            border: "none"
          }} 
          onClick={onClose}
        >
          {t('CLOSE') || 'Close'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
